import { tva } from '@gluestack-ui/utils/nativewind-utils'
import React, { ReactNode } from 'react'

import { Pressable } from '../pressable'
import { Text } from '../text'
import type { TextTone } from '../text/variants'

export type RoleButtonTone = 'command' | 'observe' | 'participate' | 'unavailable' | 'leave'
export type RoleButtonState = 'default' | 'hover' | 'pressed' | 'disabled'

export interface RoleButtonProps {
    /** Semantic role colour (Figma EmergencyEventsButtons 9915:32793). */
    tone: RoleButtonTone
    label: string
    onPress?: () => void
    /**
     * Pin a visual state for the story matrix / documentation. Omit for a live
     * button — hover and press resolve via CSS on web.
     */
    state?: RoleButtonState
    isDisabled?: boolean
    /** Optional leading / trailing glyphs (e.g. a back arrow on "leave"). */
    iconLeft?: ReactNode
    iconRight?: ReactNode
    className?: string
    /**
     * Test hook (legacy Selenium location). Rendered as `data-testid` on web by
     * react-native-web — replaces the `data-test-id` the old FormButton emitted.
     */
    testID?: string
}

// Figma button: 48px tall, 12px x-padding, 8px y-padding, 8px gap, 4px radius.
// Resting/hover/pressed fills per tone (graphics tokens); `leave` is the
// text-style button (transparent + neutral hover/press layers). `live` carries
// the web hover/press modifiers (no-op on native); the pinned states are for the
// story matrix / documentation. `disabled` reuses the resting look + opacity.
const roleButton = tva({
    base: 'h-12 flex-row items-center justify-center gap-2 rounded px-3 py-2',
    variants: {
        tone: { command: '', observe: '', participate: '', unavailable: '', leave: '' },
        state: { default: '', hover: '', pressed: '', live: '' },
        isDisabled: { true: 'opacity-40', false: '' },
    },
    compoundVariants: [
        { tone: 'command', state: 'default', class: 'bg-graphics-red' },
        { tone: 'command', state: 'hover', class: 'bg-graphics-red-hover' },
        { tone: 'command', state: 'pressed', class: 'bg-graphics-red-pressed' },
        {
            tone: 'command',
            state: 'live',
            class: 'bg-graphics-red hover:bg-graphics-red-hover active:bg-graphics-red-pressed',
        },
        { tone: 'observe', state: 'default', class: 'bg-graphics-silver' },
        { tone: 'observe', state: 'hover', class: 'bg-graphics-silver-hover' },
        { tone: 'observe', state: 'pressed', class: 'bg-graphics-silver-pressed' },
        {
            tone: 'observe',
            state: 'live',
            class: 'bg-graphics-silver hover:bg-graphics-silver-hover active:bg-graphics-silver-pressed',
        },
        { tone: 'participate', state: 'default', class: 'bg-graphics-green' },
        { tone: 'participate', state: 'hover', class: 'bg-graphics-green-hover' },
        { tone: 'participate', state: 'pressed', class: 'bg-graphics-green-pressed' },
        {
            tone: 'participate',
            state: 'live',
            class: 'bg-graphics-green hover:bg-graphics-green-hover active:bg-graphics-green-pressed',
        },
        { tone: 'unavailable', state: 'default', class: 'bg-graphics-dark-grey' },
        { tone: 'unavailable', state: 'hover', class: 'bg-graphics-dark-grey-hover' },
        { tone: 'unavailable', state: 'pressed', class: 'bg-graphics-dark-grey-pressed' },
        {
            tone: 'unavailable',
            state: 'live',
            class: 'bg-graphics-dark-grey hover:bg-graphics-dark-grey-hover active:bg-graphics-dark-grey-pressed',
        },
        { tone: 'leave', state: 'default', class: 'bg-transparent' },
        { tone: 'leave', state: 'hover', class: 'bg-aos-bg-hover-layer' },
        { tone: 'leave', state: 'pressed', class: 'bg-aos-bg-surface' },
        {
            tone: 'leave',
            state: 'live',
            class: 'bg-transparent hover:bg-aos-bg-hover-layer active:bg-aos-bg-surface',
        },
    ],
    defaultVariants: { state: 'live', isDisabled: false },
})

// Dark ink on the light fills (red/silver/green); light ink on the dark-grey
// and transparent ones. `default` is tenant-stable light (Figma content/on-action
// is near-white across tenants — unlike `on-primary`, which flips dark on AOIMS).
const toneInk: Record<RoleButtonTone, TextTone> = {
    command: 'on-graphics',
    observe: 'on-graphics',
    participate: 'on-graphics',
    unavailable: 'default',
    leave: 'default',
}

/**
 * Emergency-event role button (Figma EmergencyEventsButtons 9915:32793): the
 * command / observe / participate / not-available / leave actions of the private
 * channel invitation + status flows. Presentational and controlled — live
 * hover/press on web by default; pass `state` to pin a single look.
 */
export const RoleButton: React.FC<RoleButtonProps> = ({
    tone,
    label,
    onPress,
    state,
    isDisabled,
    iconLeft,
    iconRight,
    className,
    testID,
}) => {
    const disabled = isDisabled || state === 'disabled'
    // Pinned hover/pressed map straight through; default/disabled use the resting
    // fill; an unpinned, enabled button is `live` (CSS hover/press on web).
    const bgState: 'default' | 'hover' | 'pressed' | 'live' =
        state === 'hover' || state === 'pressed' ? state : !state && !disabled ? 'live' : 'default'

    return (
        <Pressable
            onPress={disabled ? undefined : onPress}
            accessibilityRole='button'
            disabled={disabled}
            className={roleButton({ tone, state: bgState, isDisabled: disabled, class: className })}
            testID={testID}
        >
            {iconLeft}
            <Text size='body' tone={toneInk[tone]} className='px-1'>
                {label}
            </Text>
            {iconRight}
        </Pressable>
    )
}

RoleButton.displayName = 'RoleButton'
